import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Calendar, ArrowLeft, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

// Branded confirmation page after Square checkout redirect.
// Reads ?pkg=&months= to display what was purchased and drive the next step.
export default function Success() {
  const [params] = useSearchParams();
  const [pkg, setPkg] = useState(null);

  const packageId = params.get("pkg") || "";
  const months = parseInt(params.get("months") || "1", 10);
  const transactionId = params.get("transactionId") || params.get("orderId") || "";

  useEffect(() => {
    // Fire GA conversion event if analytics is set up
    if (typeof window.gtag === "function") {
      window.gtag("event", "purchase", {
        transaction_id: transactionId || `${packageId}-${Date.now()}`,
        currency: "CAD",
        items: [{ item_id: packageId, item_name: packageId, quantity: 1 }],
      });
    }
    // Load the package so we can show a personalized message
    if (packageId) {
      api.get("/packages").then((r) => {
        const found = r.data.find((p) => p.id === packageId);
        if (found) setPkg(found);
      }).catch(() => {});
    }
  }, [packageId, transactionId]);

  const whatsAppHref = `https://wa.me/16475132225?text=${encodeURIComponent(
    `Hi Jean Claude! I just booked ${pkg?.name || "a package"} — excited to get started!`
  )}`;

  const isOneTime = pkg?.one_time;
  const nextStepText = isOneTime
    ? "Coach will reach out via email within 24 hours to schedule your session(s)."
    : "Coach will reach out within 24 hours to schedule your first session and set up your program.";

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden relative" data-testid="success-page">
      {/* Blue glow accents */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#005EEA]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-[#005EEA]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs font-mono-accent uppercase tracking-[0.2em] mb-12 transition-colors"
          data-testid="success-back-btn"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>

        {/* Big animated checkmark */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
          className="mb-8"
        >
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 bg-[#005EEA] rounded-full blur-2xl opacity-60" />
            <div className="relative w-24 h-24 bg-[#005EEA] rounded-full grid place-items-center shadow-[0_0_60px_rgba(0,94,234,0.6)]">
              <CheckCircle2 size={48} strokeWidth={2.5} className="text-white" />
            </div>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
          className="text-center mb-6"
        >
          <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">
            — PAYMENT CONFIRMED
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
            You&apos;re <span className="text-[#005EEA]">in.</span>
          </h1>
        </motion.div>

        {/* Package + next steps */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.19, 1, 0.22, 1] }}
          className="mt-10 border border-white/10 bg-black/40 backdrop-blur-sm p-6 sm:p-8"
          data-testid="success-details"
        >
          {pkg ? (
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <p className="font-mono-accent text-[10px] text-white/40 mb-2">YOU JUST PURCHASED</p>
                <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight" data-testid="success-pkg-name">
                  {pkg.name}
                </h2>
                {!isOneTime && (
                  <p className="text-white/60 text-sm mt-2 font-mono-accent">
                    {months} MONTH{months > 1 ? "S" : ""}
                  </p>
                )}
                {pkg.tagline && <p className="text-white/60 text-sm mt-2">{pkg.tagline}</p>}
              </div>
              <Sparkles className="text-[#005EEA] mt-2" size={24} />
            </div>
          ) : (
            <div className="mb-6">
              <p className="font-mono-accent text-[10px] text-white/40 mb-2">CONFIRMED</p>
              <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight">
                Your booking is locked in
              </h2>
            </div>
          )}

          <div className="border-t border-white/10 pt-6">
            <p className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.3em] mb-3">
              WHAT HAPPENS NEXT
            </p>
            <ul className="space-y-3 text-sm sm:text-base text-white/85">
              <li className="flex gap-3">
                <span className="text-[#005EEA] font-mono-accent text-xs mt-0.5">01</span>
                <span>Check your inbox — a receipt from Square is on its way.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#005EEA] font-mono-accent text-xs mt-0.5">02</span>
                <span>{nextStepText}</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#005EEA] font-mono-accent text-xs mt-0.5">03</span>
                <span>Want to fast-track? WhatsApp the coach and say hi.</span>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.45, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <a
            href={whatsAppHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#25D366] text-black py-4 px-6 hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(37,211,102,0.35)]"
            data-testid="success-whatsapp-btn"
          >
            <MessageCircle size={14} strokeWidth={2.5} fill="currentColor" />
            Message coach on WhatsApp
          </a>
          <Link
            to="/#schedule"
            className="font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/20 text-white py-4 px-6 hover:border-white hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
            data-testid="success-schedule-btn"
          >
            <Calendar size={14} />
            View class schedule
          </Link>
        </motion.div>

        {/* Referral nudge */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-12 text-center text-white/40 text-xs font-mono-accent"
        >
          — REFER A FRIEND · YOU BOTH GET A FREE WEEK —
        </motion.p>
      </div>
    </div>
  );
}
